import React, {useMemo} from 'react'
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    Paper,
    Stack,
    Typography,
} from '@mui/material'
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import Page from 'components/Page/Page'
import RateCalendarGrid from 'components/Page/Rates/RateCalendarGrid'
import useRates from 'logic/Rates/useRates'
import {FormProvider, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {useGetCurrentUserQuery, useGetRatesQuery} from 'store/Store'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import cloneDeep from 'lodash/cloneDeep'
import PageLoader from 'components/Display/Loader/PageLoader'
import {createRateField as baseRateFields} from 'constants/FormField/createRateField'
import {getDocumentId, mod} from 'utils'
import {formatHour, MONTH_PERIOD} from 'services/rates/ratesCalendarUtils'
import useRatesCalendar from 'logic/Rates/useRatesCalendar'

const RatesCalendar = () => {
    const navigate = useNavigate()
    const {t} = useTranslation()
    const {data: userToken} = useGetCurrentUserQuery()
    const {fieldsCreate, validationSchema, isLoading: isFieldsLoading} = useRates()
    const methods = useForm({
        resolver: validationSchema && zodResolver(validationSchema),
        defaultValues: {status: true},
    })
    const {watch} = methods
    const {account} = watch()

    const query = useMemo(() => {
        const accountId = getDocumentId(userToken?.account || account)
        if (accountId) {
            return {account: accountId, status: true}
        } else if (userToken?.isSuperUser) {
            return {status: true, isGlobal: true}
        }
    }, [userToken, account])
    const {data: rates} = useGetRatesQuery(query, {skip: !query})
    const fields = useMemo(() => cloneDeep(fieldsCreate || baseRateFields), [fieldsCreate])

    const {
        hoveredLockedCells,
        lockedRate,
        handleLockedCellHover,
        handleLockedCellLeave,
        handleLockedCellClick,
        closeLockedRateDialog,
        handleRectAction,
        lockedCells,
        lockedCellDetails,
        isUpdatingStatus,
        disableLockedRate,
        onSubmit,
        generalFieldConfigs,
        shouldShowCalendar,
        monthLabels,
        lockedRateMonthsHours,
        selectionSummary,
        displayedSelectedCells,
        renderFieldsGrid,
        handleClearSelection,
        selectedCells,
        isSubmitting,
    } = useRatesCalendar({rates, methods, fields})

    return (
        <Page>
            <FormProvider {...methods}>
                <Box
                    component="form"
                    onSubmit={onSubmit}
                    sx={{display: 'flex', flexDirection: 'column', gap: 2.5, width: '100%', height: 'inherit'}}>

                    {/* ── Page header ── */}
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                        <Button
                            type="button"
                            color="inherit"
                            size="small"
                            startIcon={<ArrowBackRoundedIcon sx={{fontSize: '1rem'}} />}
                            onClick={() => navigate('/rates')}
                            sx={{color: 'text.secondary', fontWeight: 500, borderRadius: 2, px: 1.5, '&:hover': {bgcolor: 'action.hover'}}}>
                            {t('rates.calendar.cancel')}
                        </Button>
                        <Divider orientation="vertical" flexItem sx={{mx: 0.5}} />
                        <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                            {t('rates.calendar.title')}
                        </Typography>
                    </Box>

                    {/* ── Two-panel layout ── */}
                    <Stack
                        direction={{xs: 'column', lg: 'row'}}
                        spacing={2.5}
                        alignItems="stretch"
                        flex={1}
                        sx={{overflow: 'hidden'}}>

                        {/* ── Settings panel ── */}
                        <Paper
                            elevation={0}
                            sx={{
                                flex: {xs: '1 1 auto', lg: '0 0 380px'},
                                display: 'flex',
                                flexDirection: 'column',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 3,
                                overflow: 'hidden',
                            }}>
                            <Box sx={{
                                px: 2.5, py: 2,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'action.hover',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.5,
                            }}>
                                <Box sx={{
                                    width: 32, height: 32,
                                    borderRadius: 2,
                                    bgcolor: 'rgba(99,102,241,0.12)',
                                    border: '1px solid rgba(99,102,241,0.2)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <TuneRoundedIcon sx={{fontSize: '1rem', color: 'primary.main'}} />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{lineHeight: 1.4}}>
                                        {t('rates.calendar.form.settingsTitle')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', lineHeight: 1.5, mt: 0.25}}>
                                        {t('rates.calendar.form.settingsHint')}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 2}}>
                                {isFieldsLoading
                                    ? <PageLoader show />
                                    : renderFieldsGrid(generalFieldConfigs, 'rate_settings')}
                                <Box>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={isSubmitting}
                                        startIcon={<SaveRoundedIcon fontSize="small" />}
                                        sx={{minWidth: 160, boxShadow: 'none', px: 3}}>
                                        {isSubmitting ? t('rates.calendar.saving') : t('rates.calendar.save')}
                                    </Button>
                                </Box>
                            </Box>
                        </Paper>

                        {/* ── Calendar panel ── */}
                        {shouldShowCalendar && (
                            <Paper
                                elevation={0}
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    p: 2.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 3,
                                    overflow: 'auto',
                                }}>

                                {/* Selection summary */}
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: 1.5,
                                    px: 2,
                                    py: 1.25,
                                    bgcolor: 'rgba(99,102,241,0.06)',
                                    border: '1px solid rgba(99,102,241,0.15)',
                                    borderRadius: 2,
                                }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectionSummary}
                                    </Typography>
                                    <Button
                                        type="button"
                                        onClick={handleClearSelection}
                                        disabled={!selectedCells.size}
                                        size="small"
                                        variant="outlined"
                                        startIcon={<RestartAltRoundedIcon sx={{fontSize: '0.9rem'}} />}
                                        sx={{borderRadius: 2, minWidth: 'max-content', fontSize: '0.78rem'}}>
                                        {t('rates.calendar.clear')}
                                    </Button>
                                </Box>

                                {/* Instructions + tips */}
                                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap'}}>
                                    <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.8rem'}}>
                                        {t('rates.calendar.instructions')}
                                    </Typography>
                                    <Box sx={{display: 'flex', gap: 0.75, flexShrink: 0}}>
                                        <Box sx={{px: 1.25, py: 0.35, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap'}}>
                                            {t('rates.calendar.tips.drag')}
                                        </Box>
                                        <Box sx={{px: 1.25, py: 0.35, borderRadius: 1.5, border: '1px solid rgba(239,68,68,0.3)', bgcolor: 'rgba(239,68,68,0.06)', fontSize: '0.72rem', fontWeight: 600, color: 'error.main', whiteSpace: 'nowrap'}}>
                                            {t('rates.calendar.tips.remove')}
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Grid */}
                                <RateCalendarGrid
                                    selectedCells={displayedSelectedCells}
                                    lockedCells={lockedCells}
                                    hoveredLockedCells={hoveredLockedCells}
                                    lockedCellDetails={lockedCellDetails}
                                    onRectAction={handleRectAction}
                                    onLockedCellClick={handleLockedCellClick}
                                    onLockedCellHover={handleLockedCellHover}
                                    onLockedCellLeave={handleLockedCellLeave}
                                />
                            </Paper>
                        )}
                    </Stack>
                </Box>
            </FormProvider>

            {/* ── Locked-rate dialog ── */}
            <Dialog
                open={Boolean(lockedRate)}
                onClose={closeLockedRateDialog}
                fullWidth
                maxWidth="xs"
                PaperProps={{sx: {borderRadius: 3, overflow: 'hidden'}}}>
                {lockedRate && (
                    <>
                        {/* Header */}
                        <Box sx={{
                            px: 3, pt: 3, pb: 2.5,
                            background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(99,102,241,0.04) 100%)',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        }}>
                            <Typography sx={{fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.75}}>
                                {t('rates.calendar.lockedRate.title')}
                            </Typography>
                            <Typography variant="h6" fontWeight={700} sx={{lineHeight: 1.3, mb: 1.25}}>
                                {lockedRate.name}
                            </Typography>
                            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center'}}>
                                <Box sx={{px: 1.5, py: 0.5, borderRadius: 2, bgcolor: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)', display: 'inline-flex', alignItems: 'baseline'}}>
                                    <Typography sx={{fontSize: '1.25rem', fontWeight: 800, color: 'primary.main', fontVariantNumeric: 'tabular-nums', lineHeight: 1}}>
                                        {lockedRate.rate != null ? lockedRate.rate : '—'}
                                    </Typography>
                                </Box>
                                {lockedRate.typeDay && (
                                    <Chip
                                        label={t(`selectOptions.${lockedRate.typeDay}`)}
                                        size="small"
                                        sx={{height: 24, fontSize: '0.72rem', fontWeight: 600, bgcolor: 'action.selected', border: '1px solid', borderColor: 'divider'}}
                                    />
                                )}
                            </Box>
                        </Box>

                        <DialogContent sx={{px: 3, py: 2.5}}>
                            <Stack spacing={2.5}>
                                <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.8rem'}}>
                                    {t('rates.calendar.lockedRate.description')}
                                </Typography>

                                {/* Months */}
                                <Box>
                                    <Typography sx={{fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'text.secondary', mb: 1}}>
                                        {t('rates.calendar.lockedRate.months')}
                                    </Typography>
                                    {lockedRateMonthsHours.months.length ? (
                                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.75}}>
                                            {lockedRateMonthsHours.months.map((month) => (
                                                <Chip
                                                    key={month}
                                                    label={monthLabels[mod(Number(month) - 1, MONTH_PERIOD)] || month}
                                                    size="small"
                                                    sx={{height: 24, fontSize: '0.72rem', fontWeight: 500}}
                                                />
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" color="text.disabled">
                                            {t('rates.calendar.lockedRate.none')}
                                        </Typography>
                                    )}
                                </Box>

                                <Divider />

                                {/* Hours */}
                                <Box>
                                    <Typography sx={{fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'text.secondary', mb: 1}}>
                                        {t('rates.calendar.lockedRate.hours')}
                                    </Typography>
                                    {lockedRateMonthsHours.hours.length ? (
                                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                            {lockedRateMonthsHours.hours.map((hour) => (
                                                <Box key={hour} sx={{px: 1, py: 0.25, borderRadius: 1, fontSize: '0.72rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider'}}>
                                                    {formatHour(hour)}
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" color="text.disabled">
                                            {t('rates.calendar.lockedRate.none')}
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                        </DialogContent>

                        <DialogActions sx={{px: 3, pb: 2.5, pt: 0, gap: 1}}>
                            <Button
                                onClick={closeLockedRateDialog}
                                size="small"
                                sx={{borderRadius: 2, color: 'text.secondary'}}>
                                {t('modal.cancel')}
                            </Button>
                            {lockedRate?.status && (
                                <Button
                                    onClick={disableLockedRate}
                                    color="error"
                                    variant="contained"
                                    size="small"
                                    disabled={isUpdatingStatus}
                                    disableElevation
                                    sx={{borderRadius: 2, px: 2, fontWeight: 600}}>
                                    {isUpdatingStatus
                                        ? t('rates.calendar.lockedRate.disabling')
                                        : t('rates.calendar.lockedRate.disable')}
                                </Button>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Page>
    )
}

export default RatesCalendar
